import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  Avatar,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime"; // Start Time & Duration Icon
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter"; // Intensity Icon
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import PoolIcon from "@mui/icons-material/Pool";
import RowingIcon from "@mui/icons-material/Rowing";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import SportsCricketIcon from "@mui/icons-material/SportsCricket";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import SportsIcon from "@mui/icons-material/Sports";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import HikingIcon from "@mui/icons-material/Hiking";
import GolfCourseIcon from "@mui/icons-material/GolfCourse";

import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { saveActivityLogData } from "../../apiservices/ApiService";
import ActivitySelection from "./ActivitySelection";
import DurationSlider from "./DurationSlider";
import TimeSelector from "./TimeSelector";
import IntensitySelector from "./IntensitySelector";
import UploadPicture from "./UploadPicture";
import PWAHeader from "../../common/PWAHeader";
import DatePickerRow from "../../common/DatePickerRow";
import PrimaryButton from "../../common/PrimaryButton";

const activities = [
  { label: "Walk", icon: <DirectionsRunIcon sx={{ fontSize: 30, color: "#ff9800" }} /> },
  { label: "Stairs", icon: <HikingIcon sx={{ fontSize: 30, color: "#9c27b0" }} /> },
  { label: "Run", icon: <DirectionsRunIcon sx={{ fontSize: 30, color: "#f44336" }} /> },
  { label: "Weights", icon: <FitnessCenterIcon sx={{ fontSize: 30, color: "#4caf50" }} /> },
  { label: "Cross Train", icon: <RowingIcon sx={{ fontSize: 30, color: "#2196f3" }} /> },
  { label: "Hike", icon: <HikingIcon sx={{ fontSize: 30, color: "#795548" }} /> },
  { label: "Cycling", icon: <DirectionsBikeIcon sx={{ fontSize: 30, color: "#3f51b5" }} /> },
  { label: "Cricket", icon: <SportsCricketIcon sx={{ fontSize: 30, color: "#ff5722" }} /> },
  { label: "Elliptical", icon: <RowingIcon sx={{ fontSize: 30, color: "#607d8b" }} /> },
  { label: "Yoga", icon: <SelfImprovementIcon sx={{ fontSize: 30, color: "#00bcd4" }} /> },
  { label: "Tennis", icon: <SportsTennisIcon sx={{ fontSize: 30, color: "#ff9800" }} /> },
  { label: "Table Tennis", icon: <SportsTennisIcon sx={{ fontSize: 30, color: "#8bc34a" }} /> },
  { label: "Dance", icon: <SportsIcon sx={{ fontSize: 30, color: "#e91e63" }} /> },
  { label: "Football", icon: <SportsSoccerIcon sx={{ fontSize: 30, color: "#9c27b0" }} /> },
  { label: "Rope Jump", icon: <RowingIcon sx={{ fontSize: 30, color: "#03a9f4" }} /> },
  { label: "Spinning", icon: <RowingIcon sx={{ fontSize: 30, color: "#ffeb3b" }} /> },
  { label: "Other", icon: <SportsIcon sx={{ fontSize: 30, color: "#9e9e9e" }} /> },
  { label: "Swimming", icon: <PoolIcon sx={{ fontSize: 30, color: "#00bcd4" }} /> },
  { label: "Aerobics", icon: <SportsIcon sx={{ fontSize: 30, color: "#ff5722" }} /> },
  { label: "Golf", icon: <GolfCourseIcon sx={{ fontSize: 30, color: "#4caf50" }} /> },
  { label: "Squash", icon: <SportsIcon sx={{ fontSize: 30, color: "#673ab7" }} /> },
  { label: "Badminton", icon: <SportsIcon sx={{ fontSize: 30, color: "#607d8b" }} /> },
  { label: "Meditation", icon: <SelfImprovementIcon sx={{ fontSize: 30, color: "#ff5722" }} /> },
  { label: "Jump Rope", icon: <RowingIcon sx={{ fontSize: 30, color: "#9e9e9e" }} /> },
];

const Fsn23LogActivity = ({ onTap }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [logTime, setLogTime] = useState(new Date());
  const [duration, setDuration] = useState(0);
  const [intensity, setIntensity] = useState("Easy");
  const [selectedActivity, setSelectedActivity] = useState("Walk");
  const [sliderOpen, setSliderOpen] = useState(false);

  const getCurrentTimeInSeconds = () => Math.floor(new Date().getTime() / 1000);

  const handlePrevWeek = () => setCurrentWeek((prev) => prev - 1);
  const handleNextWeek = () => setCurrentWeek((prev) => prev + 1);

  const generateWeekDates = (weekOffset) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      return day.toISOString().split("T")[0];
    });
  };

  const handleSubmit = async () => {
    try {
      const durationMinutes = parseInt(duration, 10) || 0;

      const endTime = new Date(logTime);
      endTime.setMinutes(logTime.getMinutes() + durationMinutes);

      const payload = {
        data: JSON.stringify([
          {
            localActivityId: `${getCurrentTimeInSeconds()}`,
            activityTypeName: selectedActivity,
            date: `${selectedDate} ${logTime.toLocaleTimeString("en-US", { hour12: false })}`,
            createdTime: new Date().toISOString(),
            startTime: logTime.toLocaleTimeString("en-US", { hour12: false }),
            endTime: endTime.toLocaleTimeString("en-US", { hour12: false }),
            duration: durationMinutes,
            durationSec: durationMinutes * 60,
            intensity,
            calories: "", // Placeholder, can be computed if needed
            status: "new",
          },
        ]),
      };

      const response = await saveActivityLogData({ data: payload });
      console.log("Activity logged successfully:", response.data);
      alert("Activity logged successfully!");
    } catch (error) {
      console.error("Error logging activity:", error);
      alert("Failed to log activity. Please try again.");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    
    <Box sx={{ padding: "16px" }}>
      <PWAHeader title={onTap?.FAI?.title || "Log Activity"} />
      <DatePickerRow
        selectedDate={selectedDate}
        onDateSelect={setSelectedDate}
        currentWeek={currentWeek}
        weekDates={generateWeekDates(currentWeek)}
        onPrevWeek={handlePrevWeek}
        onNextWeek={handleNextWeek}
      />
      <ActivitySelection
        activities={activities}
        selectedActivity={selectedActivity}
        onSelectActivity={setSelectedActivity}
      />
      <TimeSelector logTime={logTime} onChangeTime={setLogTime} />
      <Box sx={{ marginTop: "16px" }}>
        <Typography variant="body1">Duration: {duration} minutes</Typography>
        <Button variant="outlined" onClick={() => setSliderOpen(true)} sx={{ marginTop: "8px" }}>
          Select Duration
        </Button>
      </Box>
      <IntensitySelector intensity={intensity} onChangeIntensity={setIntensity} />
      <UploadPicture />
      <PrimaryButton onClick={handleSubmit} label="Log" />
      <DurationSlider open={sliderOpen} onClose={() => setSliderOpen(false)} onDurationSelect={setDuration} />
    </Box>

  </LocalizationProvider>
  );
};

export default Fsn23LogActivity;